"use client";

import React from "react";
import { Calendar, User, Tag, Folder } from "lucide-react";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div data-section-id="16654" className="w-full max-w-4xl mx-auto px-4 py-12 space-y-8 animate-pulse">
        <div className="h-12 bg-muted rounded-sm w-3/4" />
        <div className="h-6 bg-muted rounded-sm w-1/3" />
        <div className="aspect-video bg-muted rounded-sm w-full" />
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded-sm w-full" />
          <div className="h-4 bg-muted rounded-sm w-full" />
          <div className="h-4 bg-muted rounded-sm w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-destructive mb-6 font-default">Unable to retrieve insight: {error}</p>
        <Button onClick={refetch} variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
          Retry Request
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-default font-bold mb-6 text-foreground">Insight not found</h1>
        <Link to="#">
          <Button className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Return to Overview</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="w-full max-w-4xl mx-auto px-4 py-12 md:py-24">
      {}
      <header className="mb-12 text-center md:text-left space-y-8">
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-xs uppercase tracking-widest text-muted-foreground font-default">
          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center gap-2">
              <Folder className="size-3.5 text-primary" />
              <span>
                {post.categories.map((cat, i) => (
                  <span key={cat.id}>
                    {i > 0 && " / "}
                    {cat.name}
                  </span>
                ))}
              </span>
            </div>
          )}
          {formattedDate && (
            <div className="flex items-center gap-2">
              <Calendar className="size-3.5 text-primary" />
              <time dateTime={post.date}>{formattedDate}</time>
            </div>
          )}
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-foreground leading-[1.1] font-default">
          {post.title}
        </h1>

        {}
        {post.author && (
          <div className="flex items-center justify-center md:justify-start gap-4 pt-4">
            <Avatar className="size-12 border border-border rounded-none">
              <AvatarImage 
                src={post.author.avatar?.["96"] || ""} 
                alt={post.author.name} 
              />
              <AvatarFallback className="rounded-none bg-muted text-muted-foreground">{post.author.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-default font-semibold text-foreground leading-none">{post.author.name}</p>
              <p className="text-xs text-muted-foreground mt-1.5 uppercase tracking-wider">Ventures Contributor</p>
            </div>
          </div>
        )}
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-16 rounded-none overflow-hidden shadow-sm border border-border bg-muted">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover max-h-[700px] grayscale hover:grayscale-0 transition-all duration-700"
          />
        </div>
      )}

      {}
      <div 
        className="prose prose-lg dark:prose-invert max-w-none mb-20 text-foreground/80 font-default leading-relaxed [&_p]:mb-8 [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mt-16 [&_h2]:mb-8 [&_h2]:font-default [&_h2]:tracking-tight [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mt-10 [&_h3]:mb-6 [&_h3]:font-default [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-8 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-8 [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:my-12 [&_blockquote]:text-foreground [&_blockquote]:font-serif [&_blockquote]:text-2xl"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mb-16">
          <div className="flex flex-wrap gap-3">
            {post.tags.map((tag) => (
              <span 
                key={tag.id} 
                className="inline-flex items-center px-4 py-1.5 rounded-none text-[10px] uppercase tracking-widest bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-colors duration-300 font-default"
              >
                <Tag className="size-3 mr-2" />
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <Separator className="my-16 bg-border/50" />

      {}
      {post.author && (
        <section className="bg-card rounded-none p-10 border border-border shadow-none">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8 text-center md:text-left">
            <Avatar className="size-28 border border-border rounded-none shadow-sm">
              <AvatarImage 
                src={post.author.avatar?.["96"] || ""} 
                alt={post.author.name} 
              />
              <AvatarFallback className="text-3xl rounded-none bg-muted text-muted-foreground">{post.author.name.charAt(0)}</AvatarFallback>
            </Avatar>
            
            <div className="flex-1 space-y-4">
              <div>
                <h3 className="text-2xl font-bold text-card-foreground font-default tracking-tight">{post.author.name}</h3>
                <p className="text-primary text-xs uppercase tracking-widest mt-1 font-default font-medium">Operator-Led Strategist at 1066 Ventures</p>
              </div>
              
              <p className="text-muted-foreground leading-relaxed max-w-2xl font-default text-base">
                Curating high-level perspectives on growth, operations, and execution. {post.author.name} focuses on delivering the strategic clarity required to scale early-stage ventures without the typical founder burnout.
              </p>

              {post.author.link && (
                <div className="pt-4">
                  <Link to={post.author.link}>
                    <Button variant="outline" size="sm" className="gap-2 rounded-none border-primary/30 hover:border-primary text-foreground font-default uppercase tracking-widest text-[10px]">
                      <User className="size-3.5" />
                      View Contributor Profile
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen dark">
      <PostsProvider wp_query={wp_query} className="w-full">
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}